import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import {
    AuthProvider,
    GetOrganizationChecklistDto,
    GetUserDto,
    IUserPreferences,
    selectOrganizationChecklistLoadingState,
} from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Observable, distinctUntilChanged, filter, finalize, mergeMap, of, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { LocalStorageService } from '../../../../../../../../aumico-lib-core-fe/src/lib/services/localStorage.service';
import { MarkAllAsDonePreference } from '../../../../../../../../aumico-lib-users-fe/src/lib/interfaces/user-preferences.interface';
import { Paths } from '../../../../../enums/paths.enum';
import { FcpTreeItemType, Task } from '../../domain/models/fcp.model';
import { FcpTreeItem } from '../../domain/models/fcpTreeItem.model';
import {
    createChecklistForTestGroup,
    deleteChecklist,
    markAllTasksAsCompleted,
    updateChecklist,
    updateTasksForTestGroupAndItsChildren,
} from '../../domain/reducers/fcp.actions';
import {
    FcpFeatureState,
    selectActiveTestGroupItem,
    selectActiveTestItem,
    selectFcpChecklist,
    selectFliChecklists,
} from '../../domain/reducers/fcp.reducer';

type UserChecklist = { name: string; tasks: Task[] };
@UntilDestroy()
@Component({
    selector: 'app-fcp-checklist',
    templateUrl: 'fcpChecklist.component.html',
    styleUrls: ['fcpChecklist.component.scss'],
})
export class FcpChecklistComponent implements OnInit {
    FcpTreeItemType = FcpTreeItemType;
    activeTestGroupItem$: Observable<FcpTreeItem>;
    checklist$: Observable<UserChecklist>;
    showChecklistForm: boolean;
    activeTestItem$: Observable<FcpTreeItem>;
    activeTestItem: FcpTreeItem;
    selectedChecklist: Partial<{ tasks: Task[]; checklistName: string }>;
    selectedChecklistFromModal: GetOrganizationChecklistDto; // Add this line

    activeTestGroupItem: FcpTreeItem;
    organizationChecklist: GetOrganizationChecklistDto[];
    private currentLocale: string;
    private isSubmitting: boolean = false;
    private checklistPopupShownMap: Map<string, boolean> = new Map<string, boolean>();

    translatedText: string;
    displayChecklistSelectionModal: boolean = false;
    markTaskToRemove: string[] = [];

    @Input() expanded: boolean = true;
    @Output() expandedChange = new EventEmitter<boolean>();

    @Input() currentUser: GetUserDto;
    dialogData: { checklists: GetOrganizationChecklistDto[]; title: string };
    showMarkAllAsDoneDialog: boolean;
    userPreferences: IUserPreferences;
    currentCheckedTask: string;
    showDeleteChecklistDialog: boolean;

    constructor(
        private readonly store: Store<FcpFeatureState>,
        public dialog: MatDialog,
        public readonly i18n: I18NextService,
        private readonly localStorageService: LocalStorageService,
        private readonly authProvider: AuthProvider,
    ) {
        this.currentLocale = this.i18n.language;
        this.translatedText = this.buildTranslation();
        this.userPreferences = this.localStorageService.getUserPrefrences() ?? {};
        this.userPreferences.dontShowAgainConfirmationModalToMarkAllTasksDone =
            this.userPreferences.dontShowAgainConfirmationModalToMarkAllTasksDone ?? ({} as MarkAllAsDonePreference);
    }

    ngOnInit() {
        this.activeTestGroupItem$ = this.store.select(selectActiveTestGroupItem);
        this.activeTestItem$ = this.store.select(selectActiveTestItem);

        this.checklist$ = this.activeTestGroupItem$.pipe(
            filter((activeTestGroupItem) => activeTestGroupItem != null),
            tap((activeTestGroupItem) => {
                this.activeTestGroupItem = cloneDeep(activeTestGroupItem);
            }),
            mergeMap((activeTestGroupItem) => {
                if (activeTestGroupItem.tasks.length > 0) {
                    return of({
                        name: activeTestGroupItem.checklistname,
                        tasks: cloneDeep(activeTestGroupItem.tasks),
                    });
                } else {
                    return this.getFliChecklist$(
                        activeTestGroupItem.treeItemName,
                        activeTestGroupItem.isDefaultChecklistDeleted,
                    );
                }
            }),
            mergeMap((groupChecklist) => {
                return this.activeTestItem$.pipe(
                    distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                        fastDeepEqual(previousFormChanges, currentFormChanges),
                    ),
                    tap((item) => (this.activeTestItem = cloneDeep(item))),

                    mergeMap((item) => {
                        if (item == null || !item.tasks?.length) {
                            return of(groupChecklist);
                        } else {
                            const activeTestChecklist: UserChecklist = {
                                name: item.checklistname,
                                tasks: cloneDeep(item.tasks),
                            };

                            if (groupChecklist && groupChecklist.tasks?.length > 0) {
                                const mergedTasks = this.mergeTasksByName(
                                    groupChecklist.tasks,
                                    activeTestChecklist.tasks,
                                );
                                activeTestChecklist.tasks = mergedTasks;
                            }

                            this.activeTestItem.tasks = activeTestChecklist.tasks;

                            return of(activeTestChecklist);
                        }
                    }),
                );
            }),

            finalize(() => (this.markTaskToRemove = [])),
        );

        this.store
            .select(selectFcpChecklist)
            .pipe(untilDestroyed(this))
            .subscribe((checklists) => {
                this.organizationChecklist = checklists;
            });

        // Listen for success or failure actions related to checklist creation or update
        this.store
            .select(selectOrganizationChecklistLoadingState) // Modify selector based on your state structure
            .pipe(untilDestroyed(this))
            .subscribe((isLoading) => {
                if (!isLoading) {
                    this.isSubmitting = false; // Reset the flag on success or failure
                }
            });
    }

    onCreateCheckList(): void {
        this.selectedChecklist = null;
        this.showChecklistForm = true;
    }

    onCancel(): void {
        this.showChecklistForm = false;
        this.markTaskToRemove = [];
    }

    onSubmit($event: Task[]): void {
        if (this.isSubmitting) return;
        this.isSubmitting = true;

        const tasks = $event;
        const unsealedTasks = this.getValidatedUnsealedTasks(tasks);

        this.removeMarkedTasks();

        const updatedGroupTasks = this.getUpdatedGroupTasks(unsealedTasks);
        const newTasks = this.getNewTasks(unsealedTasks, updatedGroupTasks);

        this.dispatchGroupTaskUpdate(updatedGroupTasks, newTasks);
        if (this.activeTestItem == null) {
            this.showChecklistForm = false;
            this.isSubmitting = false;
            return;
        }

        let activeTestItemTask: Task[] = [];
        if (this.activeTestItem.tasks?.length) {
            activeTestItemTask = tasks
                .filter((task) => task.id != null)
                .map((task, index) => {
                    return {
                        ...this.activeTestItem.tasks[index],
                        text: task.text,
                    };
                });
            activeTestItemTask = [...activeTestItemTask, ...newTasks];
        } else {
            activeTestItemTask = [...tasks];
        }

        this.store.dispatch(
            updateChecklist({ tasks: activeTestItemTask, checklistName: this.selectedChecklist?.checklistName }),
        );

        this.resetForm();
    }

    private getValidatedUnsealedTasks(tasks: Task[]): Task[] {
        return tasks.filter((task) => !task.isSealed || !this.markTaskToRemove.includes(task.text));
    }

    private removeMarkedTasks(): void {
        if (this.markTaskToRemove.length > 0) {
            this.activeTestGroupItem = {
                ...this.activeTestGroupItem,
                tasks: this.filterTasksByText(this.activeTestGroupItem.tasks, this.markTaskToRemove),
            };

            if (this.activeTestItem) {
                this.activeTestItem = {
                    ...this.activeTestItem,
                    tasks: this.filterTasksByText(this.activeTestItem.tasks, this.markTaskToRemove),
                };
            }
        }
    }

    private filterTasksByText(tasks: Task[], tasksToRemove: string[]): Task[] {
        return tasks.filter((task) => !tasksToRemove.includes(task.text));
    }

    private getUpdatedGroupTasks(unsealedTasks: Task[]): Task[] {
        const existingGroupTasks = this.activeTestGroupItem.tasks || [];

        return existingGroupTasks.map((task) => {
            const updatedTask = unsealedTasks.find((updatedTasks) => updatedTasks.id === task.id);
            return {
                ...task,
                text: updatedTask?.text ?? task.text,
            };
        });
    }

    private getNewTasks(unsealedTasks: Task[], existingTasks: Task[]): Task[] {
        return unsealedTasks
            .filter((task) => !task.id && !existingTasks.some((existingTask) => existingTask.text === task.text))
            .map((task) => ({
                ...task,
                id: uuidv4(),
                createdAt: new Date(),
                createdBy: this.currentUser.id,
            }));
    }

    private dispatchGroupTaskUpdate(updatedTasks: Task[], newTasks: Task[]): void {
        if (!this.activeTestGroupItem.tasks?.length) {
            this.store.dispatch(
                createChecklistForTestGroup({
                    tasks: [...updatedTasks, ...newTasks],
                    checklistName: null,
                }),
            );
        } else {
            this.store.dispatch(
                updateTasksForTestGroupAndItsChildren({
                    updatedTasks,
                    createdTasks: newTasks,
                    deletedTasks: this.markTaskToRemove,
                }),
            );
        }
    }

    private resetForm(): void {
        this.isSubmitting = false;
        this.showChecklistForm = false;
        this.markTaskToRemove = [];
    }

    onTaskChange($event: MatCheckboxChange, checklist: UserChecklist, task: Task, index: number): void {
        if (!this.activeTestItem && !task.isDone) {
            this.currentCheckedTask = task.text;
            this.onMarkAllAsCompleted();
            return;
        } else if (this.activeTestItem != null) {
            const isChecked = $event.checked;
            task.isDone = isChecked;
            task.isSealed = isChecked;
            if (!this.activeTestItem.tasks.length) {
                this.activeTestItem.tasks = checklist.tasks;
                this.activeTestItem.checklistname = checklist.name;
            }

            this.activeTestItem.tasks[index].isDone = isChecked;
            this.activeTestItem.tasks[index].isSealed = isChecked;

            if (isChecked) {
                this.activeTestItem.tasks[index].doneAt = new Date();
                this.activeTestItem.tasks[index].doneBy = this.currentUser.id;
                this.activeTestItem.tasks[index].doneByName =
                    this.currentUser.firstName + ' ' + this.currentUser.lastName;
            }
            this.store.dispatch(
                updateChecklist({
                    tasks: cloneDeep(this.activeTestItem.tasks),
                    checklistName: this.activeTestItem.checklistname,
                }),
            );
        }
    }

    onEditChecklist($event: UserChecklist): void {
        this.selectedChecklist = {
            tasks: $event.tasks,
            checklistName: $event.name,
        };
        this.showChecklistForm = true;
    }

    onImportChecklist(): void {
        this.showChecklistSelectionModal(
            this.organizationChecklist,
            this.i18n.t('aumico_reports_import_checklist') as string,
        );
    }

    onManyChecklists(checklists: GetOrganizationChecklistDto[]): void {
        const treeItemName = this.activeTestItem?.name ?? this.activeTestGroupItem?.treeItemName;
        if (!treeItemName) {
            return; // If treeItemName is not available, return early
        }

        // Check if the popup has already been shown for this specific treeItemName
        if (!this.checklistPopupShownMap.get(treeItemName)) {
            this.checklistPopupShownMap.set(treeItemName, true); // Set it to true as it is being shown now
            this.showChecklistSelectionModal(checklists, this.i18n.t('aumico_reports_select_checklist') as string);
        }
    }

    showChecklistSelectionModal(checklists: GetOrganizationChecklistDto[], title: string): void {
        this.displayChecklistSelectionModal = true;
        this.dialogData = { checklists, title };
    }

    onCancelChecklistModal() {
        this.displayChecklistSelectionModal = false;
    }
    onRowInModalSelected(selectedRow: GetOrganizationChecklistDto) {
        this.selectedChecklistFromModal = selectedRow;
    }

    onSelectChecklistFromModal() {
        this.displayChecklistSelectionModal = false;
        const result = this.selectedChecklistFromModal;

        const tasks = result.tasks.map((task) => ({
            text: task,
            isDone: false,
            id: uuidv4(),
            createdAt: new Date(),
            createdBy: this.currentUser.id,
            dueDate: null,
        }));

        if (!this.activeTestGroupItem.tasks?.length) {
            this.store.dispatch(createChecklistForTestGroup({ tasks: cloneDeep(tasks), checklistName: result.name }));
        }

        if (this.activeTestItem != null) {
            this.store.dispatch(updateChecklist({ tasks: cloneDeep(tasks), checklistName: result.name }));
        }
    }

    private buildTranslation(): string {
        const linkUrl = `/${this.currentLocale}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}/${Paths.CHECKLISTS}`;
        const linkText = this.i18n.t('aumico_reports_custom_checklist_creation_link_text');

        return this.i18n.t('aumico_reports_custom_checklist_creation_text', {
            linkText: `<a href="${linkUrl}">${linkText}</a>`,
        }) as string;
    }

    updateCardExpandedState(expanded: boolean) {
        this.expandedChange.emit(this.expanded); // Emit the new state
    }

    public readonly onMarkTaskToRemove = (task: string) => {
        this.markTaskToRemove.push(task);
    };

    private readonly getFliChecklist$ = (fliName: string, isDefaultChecklistDeletedBefore: boolean) => {
        return this.store.select(selectFliChecklists(fliName)).pipe(
            filter((checklists) => checklists != null),
            mergeMap((checkLists) => {
                if (checkLists.length === 0) {
                    return of(null);
                } else if (checkLists.length > 1) {
                    this.onManyChecklists(checkLists);
                    return of(null);
                } else if (!isDefaultChecklistDeletedBefore) {
                    const tasks = checkLists[0].tasks.map(
                        (checklistTask) =>
                            ({
                                text: checklistTask,
                                isDone: false,
                                id: uuidv4(),
                                createdAt: new Date(),
                                createdBy: this.currentUser.id,
                                dueDate: null,
                            } as Task),
                    );
                    const checklist: UserChecklist = {
                        name: checkLists[0].name,
                        tasks,
                    };

                    this.store.dispatch(createChecklistForTestGroup({ tasks, checklistName: checkLists[0].name }));

                    return of(checklist);
                }

                return of(null);
            }),
        );
    };
    private readonly mergeTasksByName = (groupTasks: Task[], activeTasks: Task[]) => {
        const getKey = (task: Task, index: number) => (task.text && task.text.trim() ? task.text : `__EMPTY__${index}`); // Generate unique keys for empty text

        const activeTaskMap = new Map(activeTasks.map((task, index) => [getKey(task, index), task]));

        groupTasks.forEach((groupTask, index) => {
            const key = getKey(groupTask, index);
            if (!activeTaskMap.has(key)) {
                activeTaskMap.set(key, groupTask);
            }
        });

        return Array.from(activeTaskMap.values());
    };

    onMarkAllAsCompleted() {
        const { dontShowAgain, action } = this.userPreferences.dontShowAgainConfirmationModalToMarkAllTasksDone;

        if (!dontShowAgain) {
            this.showMarkAllAsDoneDialog = true;
        } else if (dontShowAgain && action === 'yes') {
            this.onConfirmMarkTaskInAllAccountsAsDone();
        }
    }

    onDeleteChecklist() {
        this.store.dispatch(deleteChecklist());
        this.showDeleteChecklistDialog = false;
    }

    onConfirmMarkTaskInAllAccountsAsDone = () => {
        this.showMarkAllAsDoneDialog = false;
        if (!this.currentCheckedTask) return;

        this.store.dispatch(
            markAllTasksAsCompleted({
                doneByName: `${this.currentUser.firstName} ${this.currentUser.lastName}`,
                doneBy: this.currentUser.id,
                task: this.currentCheckedTask,
            }),
        );
        this.currentCheckedTask = null;

        this.setUserPrefernce('yes');
    };

    onDontAskAgainChange = ($event: boolean) => {
        this.userPreferences.dontShowAgainConfirmationModalToMarkAllTasksDone.dontShowAgain = $event;
        this.localStorageService.setUserPrefrences(this.userPreferences);
    };

    onCancelMarkTaskInAllAccountsAsDone = () => {
        this.showMarkAllAsDoneDialog = false;
        this.setUserPrefernce('no');
    };

    private setUserPrefernce = (action: 'yes' | 'no'): void => {
        const { dontShowAgain } = this.userPreferences.dontShowAgainConfirmationModalToMarkAllTasksDone;
        if (dontShowAgain) {
            this.userPreferences.dontShowAgainConfirmationModalToMarkAllTasksDone.action = action;
            this.localStorageService.setUserPrefrences(this.userPreferences);
            this.authProvider.dispatchUpdateUserPreferences({
                ...this.userPreferences,
                dontShowAgainConfirmationModalToMarkAllTasksDone:
                    this.userPreferences.dontShowAgainConfirmationModalToMarkAllTasksDone,
            });
        }
    };
}
